import React, { useContext, useLayoutEffect, useRef, useState } from 'react';
import { ThemeContext } from '../context/ThemeContext';
import {
  getProductionPwaUrl,
  isDesktopPwaTarget,
  isLocalDevOrigin,
  isRunningAsInstalledClient,
  LOCALHOST_PWA_BANNER_CSS_VAR,
  LOCALHOST_PWA_WARNING_DISMISS_KEY,
  PRODUCTION_PWA_ORIGIN,
} from '../utils/pwa';

/**
 * Aviso cuando la PWA se instaló o se intenta instalar desde localhost
 * (la app queda atada a npm start + backend local).
 */
function clearBannerOffset() {
  if (typeof document === 'undefined') return;
  document.documentElement.style.removeProperty(LOCALHOST_PWA_BANNER_CSS_VAR);
}

export default function LocalhostPwaWarning() {
  const { modoOscuro } = useContext(ThemeContext);
  const bannerRef = useRef(null);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(LOCALHOST_PWA_WARNING_DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });

  const shouldShow = !dismissed && isLocalDevOrigin() && isDesktopPwaTarget();

  useLayoutEffect(() => {
    if (!shouldShow) {
      clearBannerOffset();
      return undefined;
    }
    const el = bannerRef.current;
    if (!el) return undefined;

    const syncHeight = () => {
      document.documentElement.style.setProperty(
        LOCALHOST_PWA_BANNER_CSS_VAR,
        `${el.offsetHeight}px`,
      );
    };
    syncHeight();
    const observer = new ResizeObserver(syncHeight);
    observer.observe(el);
    return () => {
      observer.disconnect();
      clearBannerOffset();
    };
  }, [shouldShow]);

  if (!shouldShow) return null;

  const installedHere = isRunningAsInstalledClient();
  const prodUrl = getProductionPwaUrl('/login');

  const box = modoOscuro
    ? 'bg-amber-950/90 border-amber-700 text-amber-50'
    : 'bg-amber-50 border-amber-300 text-amber-950';

  const dismiss = () => {
    try {
      sessionStorage.setItem(LOCALHOST_PWA_WARNING_DISMISS_KEY, '1');
    } catch {
      /* ignore */
    }
    setDismissed(true);
    clearBannerOffset();
  };

  return (
    <div
      ref={bannerRef}
      className={`fixed top-0 left-0 right-0 z-30 border-b px-4 py-3 pr-10 text-sm ${box}`}
      role="alert"
    >
      <button
        type="button"
        onClick={dismiss}
        className="absolute top-2 right-2 rounded px-2 py-0.5 text-lg leading-none opacity-80 hover:opacity-100"
        aria-label="Cerrar aviso de desarrollo local"
      >
        ×
      </button>
      <p className="font-semibold">
        {installedHere
          ? 'Esta app de escritorio está ligada a localhost'
          : 'La app de escritorio no se instala desde desarrollo local'}
      </p>
      <p className="mt-1 text-xs leading-relaxed opacity-95">
        {installedHere ? (
          <>
            Por eso solo funciona con <code className="font-mono">npm start</code> y el backend en la terminal.
            Desinstalá &quot;Contsuite&quot; en Windows (Configuración → Aplicaciones), abrí{' '}
            <a href={prodUrl} className="font-semibold underline underline-offset-2">
              {PRODUCTION_PWA_ORIGIN}
            </a>{' '}
            en Chrome o Edge e instalá de nuevo desde ahí.
          </>
        ) : (
          <>
            Si instalás desde <code className="font-mono">localhost:3000</code>, el ícono del escritorio seguirá
            necesitando tus servidores locales. Para la app real, entrá a{' '}
            <a href={prodUrl} className="font-semibold underline underline-offset-2">
              {PRODUCTION_PWA_ORIGIN}
            </a>{' '}
            e instalá desde el navegador.
          </>
        )}
      </p>
    </div>
  );
}
